import { Form, Radio } from "antd";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import { MdNightlightRound, MdOutlineLightMode } from "react-icons/md";
import layoutCenterImg from "../../assets/images/layout-center.png";
import layoutLeftImg from "../../assets/images/layout-left.png";
import layoutRightImg from "../../assets/images/layout-right.png";
import { TOption } from "../../types/common.type";
import { TCreateWebsite } from "../../types/website.type";
import MultiLangInput from "../MultiLangInput";

type TLayoutOption = TOption<TCreateWebsite["menu_layout"]> & {
  icon: string;
};

type TThemeOption = TOption<TCreateWebsite["theme"]> & {
  icon: IconType;
};

const Generalsettings = () => {
  const { t } = useTranslation();
  const theme = Form.useWatch("theme");
  const layoutList: TLayoutOption[] = [
    {
      value: "left",
      label: t("formSetup.generalSetting.layoutLeft"),
      icon: layoutLeftImg,
    },
    {
      value: "center",
      label: t("formSetup.generalSetting.layoutCenter"),
      icon: layoutCenterImg,
    },
    {
      value: "right",
      label: t("formSetup.generalSetting.layoutRight"),
      icon: layoutRightImg,
    },
  ];
  const themeList: TThemeOption[] = [
    {
      value: "light",
      label: t("formSetup.generalSetting.themeLight"),
      icon: MdOutlineLightMode,
    },
    {
      value: "dark",
      label: t("formSetup.generalSetting.themeDark"),
      icon: MdNightlightRound,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
        <span className="block sm:w-36 text-left w-full before:content-['*'] before:text-error before:mr-1 before:inline-block before:text-[14px] before:font-[SimSun] before:leading-none">
          {t("formSetup.generalSetting.defaultLanguage")}
        </span>
        <Form.Item
          name="default_language"
          layout="horizontal"
          className="mb-0 flex-1 w-full"
        >
          <Radio.Group
            name="default_language"
            className="flex items-center gap-0 sm:gap-10 lg:gap-20 justify-between md:justify-start"
          >
            <Radio value="vi">Tiếng Việt</Radio>
            <Radio value="en">English</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
      <MultiLangInput
        name="title"
        nameEn="title_en"
        label={t("formSetup.generalSetting.name")}
        placeholder={t("formSetup.generalSetting.namePlaceholder")}
        placeholderEn="Enter website title in English"
        required={true}
      />
      <MultiLangInput
        name="slogan"
        nameEn="slogan_en"
        label={t("formSetup.generalSetting.slogan")}
        placeholder={t("formSetup.generalSetting.sloganPlaceholder")}
        placeholderEn="Enter website slogan in English"
        required={true}
      />
      <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
        <span className="block sm:w-32 text-left w-full">
          {t("formSetup.generalSetting.layout")}
        </span>
        <Form.Item
          name="menu_layout"
          layout="horizontal"
          className="mb-0 flex-1 w-full"
        >
          <Radio.Group
            name="menu_layout"
            className="flex items-center gap-0 sm:gap-10 lg:gap-20 justify-between md:justify-start"
          >
            {layoutList.map((item) => {
              return (
                <div
                  className="flex flex-col justify-center items-center"
                  key={item.value}
                >
                  <Radio className="cursor-default text-gray-600" disabled>
                    <img
                      src={item.icon as string}
                      alt="layout-img"
                      className="object-contain w-20 sm:w-32"
                    />
                  </Radio>
                  <Radio value={item.value} className="text-nowrap">
                    {item.label}
                  </Radio>
                </div>
              );
            })}
          </Radio.Group>
        </Form.Item>
      </div>
      <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
        <span className="block sm:w-32 text-left w-full">
          {t("formSetup.generalSetting.theme")}
        </span>
        <Form.Item
          name="theme"
          layout="horizontal"
          className="mb-0 flex-1 w-full"
        >
          <Radio.Group
            name="theme"
            className="flex sm:grid sm:grid-cols-3 justify-between gap-1"
          >
            {themeList.map((item) => {
              const isSelected = theme === item.value;
              return (
                <div
                  className={`flex items-center rounded-lg transition-colors ${
                    isSelected
                      ? "bg-gray-100 dark:bg-bg-tertiary"
                      : "bg-transparent"
                  }`}
                  key={item.value}
                >
                  <Radio value={item.value} className="[&_.ant-radio]:hidden">
                    <div
                      className={`cursor-pointer flex items-center rounded-lg transition-colors text-gray-600 z-10 ${
                        isSelected
                          ? "bg-gray-100 dark:bg-bg-tertiary"
                          : "bg-white dark:bg-bg-secondary"
                      }`}
                    >
                      <item.icon className="text-4xl sm:text-6xl -rotate-[30]" />
                      <span className={`${isSelected ? "font-semibold" : ""} ml-2`}>
                        {item.label}
                      </span>
                    </div>
                  </Radio>
                </div>
              );
            })}
          </Radio.Group>
        </Form.Item>
      </div>
    </div>
  );
};

export default Generalsettings;
